import React, { useContext, useState } from 'react';
import { AuthContext } from '../App';
import { Smartphone, Shield, Zap, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../services/firebase';
import { setDocument } from '../services/firestoreService';

const RegisterPage = () => {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showManualForm, setShowManualForm] = useState(false);
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleGoogleRegister = async () => {
        setIsLoading(true);
        setError('');
        try {
            await login();
        } catch (err) {
            console.error('Register error:', err);
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Pendaftaran dibatalkan. Silakan coba lagi.');
            } else if (err.code === 'auth/popup-blocked') {
                setError('Popup diblokir. Silakan izinkan popup untuk domain ini.');
            } else {
                setError('Gagal mendaftar. Silakan coba lagi.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleManualRegister = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirmPassword) {
            setError('Konfirmasi password tidak cocok.');
            return;
        }
        if (password.length < 6) {
            setError('Password minimal 6 karakter.');
            return;
        }
        setIsLoading(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // Update displayName di Auth
            await updateProfile(userCredential.user, { displayName: fullName });
            // Reload user agar displayName terupdate di auth.currentUser
            await auth.currentUser.reload();
            // Ambil ulang user yang sudah update
            const updatedUser = auth.currentUser;
            // Update root Firestore user dengan data yang sudah pasti ada
            await setDocument('users', updatedUser.uid, { email: updatedUser.email, displayName: updatedUser.displayName }, true);
            // Tambahkan dokumen pengaturan/utama agar user muncul di admin
            await setDocument(`users/${updatedUser.uid}/pengaturan`, 'utama', {
              email: updatedUser.email,
              displayName: updatedUser.displayName,
              isPremium: false,
              premiumUntil: null,
              trialStart: new Date().toISOString(),
              trialEnd: null
            });
            // Redirect ke dashboard atau login otomatis
            navigate('/dashboard');
        } catch (err) {
            if (err.code === 'auth/email-already-in-use') {
                setError('Email sudah terdaftar. Silakan login atau gunakan email lain.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Format email tidak valid.');
            } else {
                setError('Gagal mendaftar. Silakan coba lagi.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
            <div className="relative w-full max-w-4xl flex flex-col lg:flex-row bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden">
                {/* Mobile Header */}
                <div className="lg:hidden flex flex-col items-center justify-center p-8 bg-gradient-to-br from-[#8A78F0] to-[#6C52EE] text-white text-center">
                    <div className="flex items-center gap-3 mb-4">
                        <h1 className="text-3xl font-bold">LakuKelas</h1>
                    </div>
                    <p className="text-white/90 text-sm mb-6">Asisten Guru Cerdas</p>
                    <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4"/>
                            <span>Mobile Ready</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4"/>
                            <span>Secure</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4"/>
                            <span>Fast</span>
                        </div>
                    </div>
                </div>

                {/* Desktop Sidebar */}
                <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 bg-gradient-to-br from-[#8A78F0] to-[#6C52EE] text-white">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold">LakuKelas</h1>
                        </div>
                        <p className="mt-2 text-sm text-white/80">Asisten Guru Cerdas</p>
                    </div>
                    <div className="space-y-6">
                        <h2 className="text-4xl font-extrabold leading-tight">Bergabung dengan Kami!</h2>
                        <p className="text-white/80 text-lg leading-relaxed">Mulai perjalanan Anda untuk mengelola administrasi kelas dengan lebih mudah dan efisien.</p>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-green-300" />
                                <span>Gratis 7 hari trial</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-green-300" />
                                <span>Semua fitur tersedia</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <CheckCircle className="w-5 h-5 text-green-300" />
                                <span>Dukungan 24/7</span>
                            </div>
                        </div>
                    </div>
                    <div></div>
                </div>

                {/* Register Form Section */}
                <div className="w-full lg:w-1/2 p-6 sm:p-10 lg:p-12 flex flex-col justify-center">
                    {/* Back to Login */}
                    <div className="mb-6">
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-[#8A78F0] transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Kembali ke Login
                        </Link>
                    </div>

                    {/* Mobile Welcome Text */}
                    <div className="lg:hidden text-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Bergabung dengan LakuKelas</h2>
                        <p className="text-gray-500 dark:text-gray-400">Buat akun baru untuk mulai menggunakan aplikasi</p>
                    </div>

                    {/* Desktop Welcome Text */}
                    <div className="hidden lg:block">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Buat Akun Baru</h2>
                        <p className="text-gray-500 dark:text-gray-400 mb-8">Pilih metode pendaftaran yang paling nyaman untuk Anda.</p>
                    </div>

                    <div className="space-y-6">
                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        )}

                        {!showManualForm ? (
                            <>
                                {/* Google Sign-In Button */}
                                <button 
                                    onClick={handleGoogleRegister}
                                    disabled={isLoading}
                                    className="group w-full inline-flex items-center justify-center px-6 py-4 border border-gray-200 dark:border-gray-600 rounded-2xl text-base font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                                    ) : (
                                        <svg className="w-5 h-5 mr-3 transition-transform group-hover:scale-110" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                                            <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 23.4 172.9 61.9l-65.7 64.9C337 97.2 295.1 84 248 84c-81.6 0-148.8 67.2-148.8 148.8s67.2 148.8 148.8 148.8c99.1 0 128.8-72.2 132.8-106.8H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                                        </svg>
                                    )}
                                    {isLoading ? 'Memproses...' : 'Daftar dengan Google'}
                                </button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">atau</span>
                                    </div>
                                </div>

                                {/* Manual Register Option */}
                                <button 
                                    onClick={() => setShowManualForm(true)}
                                    className="w-full inline-flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl text-base font-semibold text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-200"
                                >
                                    Daftar dengan Email & Password
                                </button>
                            </>
                        ) : (
                            <>
                                {/* Manual Register Form */}
                                <form onSubmit={handleManualRegister} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nama Lengkap
                                        </label>
                                        <input 
                                            type="text" 
                                            required
                                            value={fullName}
                                            onChange={e => setFullName(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#8A78F0] focus:border-transparent"
                                            placeholder="Masukkan nama lengkap Anda"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Email
                                        </label>
                                        <input 
                                            type="email" 
                                            required
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#8A78F0] focus:border-transparent"
                                            placeholder="contoh@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Password
                                        </label>
                                        <input 
                                            type="password" 
                                            required
                                            minLength={6}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#8A78F0] focus:border-transparent"
                                            placeholder="Minimal 6 karakter"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Konfirmasi Password
                                        </label>
                                        <input 
                                            type="password" 
                                            required
                                            minLength={6}
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#8A78F0] focus:border-transparent"
                                            placeholder="Ulangi password Anda"
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <button 
                                            type="button"
                                            onClick={() => setShowManualForm(false)}
                                            className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Kembali
                                        </button>
                                        <button 
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex-1 px-6 py-3 bg-[#8A78F0] text-white rounded-xl hover:bg-[#6C52EE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isLoading ? 'Memproses...' : 'Daftar'}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}

                        {/* Features List */}
                        <div className="space-y-3 pt-4">
                            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span>Kelola absensi siswa dengan mudah</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span>Catat jurnal mengajar harian</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <span>Input dan analisis nilai siswa</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                <span>Generate laporan otomatis</span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                Sudah punya akun?{' '}
                                <Link to="/" className="text-[#8A78F0] hover:underline">
                                    Masuk di sini
                                </Link>
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                                Dengan mendaftar, Anda menyetujui{' '}
                                <Link to="/terms" className="text-[#8A78F0] hover:underline">
                                    Syarat & Ketentuan
                                </Link>
                                {' '}dan{' '}
                                <Link to="/privacy" className="text-[#8A78F0] hover:underline">
                                    Kebijakan Privasi
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage; 